import { differenceInDays } from 'date-fns';

import i18n from '@/i18n/config';

import type { FakturaStatus } from './FakturaStatus';
import type { IFaktura } from './IFaktura';
import type { IFakturaInput } from './IFakturaInput';

export class Faktura implements IFaktura {
    public static parseObject(obj: IFakturaInput): Faktura {
        try {
            return new Faktura(obj);
        } catch (e) {
            throw new Error('Not able to parse object');
        }
    }

    public get betaltDato() {
        return this._betaltDato;
    }

    public get byggNummer() {
        return this._byggNummer;
    }

    public get fakturaDato() {
        return this._fakturaDato;
    }

    public get fakturaNummer() {
        return this._fakturaNummer;
    }

    public get forfallsDato() {
        return this._forfallsDato;
    }

    public get id() {
        return this._id;
    }

    public get kid() {
        return this._kid;
    }

    public get kundeNummer() {
        return this._kundeNummer;
    }

    public get pdfFileName() {
        return this._pdfFileName;
    }

    public get sisteInnbetaling() {
        return this._sisteInnbetaling;
    }

    public get sum() {
        return this._sum;
    }

    public get sumInnbetalt() {
        return this._sumInnbetalt;
    }

    public get sumUtestaaende() {
        return this._sumUtestaaende;
    }

    public get sumUtestaaendeForsinket() {
        return this._sumUtestaaendeForsinket;
    }

    public get sumUtestaaendeForsinketDager() {
        return this._sumUtestaaendeForsinketDager;
    }

    public get statusCode() {
        return this._statusCode;
    }
    public get download() {
        return this._download;
    }
    public get preview() {
        return this._preview;
    }
    public get valuta() {
        return this._valuta;
    }
    public get forsinketDager() {
        return this._forsinketDager;
    }

    private _betaltDato: Date | null;
    private _byggNummer: string;
    private _fakturaDato: Date;
    private _fakturaNummer: string;
    private _forfallsDato: Date;
    private _id: string;
    private _kid: string;
    private _kundeNummer: string;
    private _pdfFileName: string;
    private _sisteInnbetaling: Date | null;
    private _sum: number;
    private _sumInnbetalt: number;
    private _sumUtestaaende: number;
    private _sumUtestaaendeForsinket: number;
    private _sumUtestaaendeForsinketDager: number;

    private _statusCode: FakturaStatus;
    private _download: string | null;
    private _preview: string | null;
    private _forsinketDager: number;
    private _valuta: string;

    public static translationStatus = (statusCode: string) => {
        return i18n.t(`fakturaStatus.${statusCode}`);
    };

    constructor(obj: IFakturaInput) {
        const {
            betaltDato,
            byggNummer,
            fakturaDato,
            fakturaNummer,
            forfallsDato,
            id,
            kid,
            kundeNummer,
            pdfFileName,
            sisteInnbetaling,
            sum,
            sumInnbetalt,
            sumUtestaaende,
            sumUtestaaendeForsinket,
            sumUtestaaendeForsinketDager,
            statusCode,
            valuta,
        } = obj;

        this._betaltDato =
            new Date(betaltDato) > new Date('2001-01-01')
                ? new Date(betaltDato)
                : null;
        this._byggNummer = byggNummer;
        this._fakturaDato = new Date(fakturaDato);
        this._fakturaNummer = fakturaNummer;

        const dueDate = new Date(forfallsDato);
        dueDate.setDate(dueDate.getDate() + 1);
        dueDate.setSeconds(dueDate.getSeconds() - 1);
        this._forfallsDato = dueDate;

        this._id = id;
        this._kid = kid;
        this._kundeNummer = kundeNummer;
        this._pdfFileName = pdfFileName;
        this._sisteInnbetaling =
            sisteInnbetaling &&
            new Date(sisteInnbetaling) > new Date('2001-01-01')
                ? new Date(sisteInnbetaling)
                : null;
        this._sum = sum;
        this._sumInnbetalt = sumInnbetalt;
        this._sumUtestaaende = sumUtestaaende;
        this._sumUtestaaendeForsinket = sumUtestaaendeForsinket;
        this._sumUtestaaendeForsinketDager = sumUtestaaendeForsinketDager;

        this._statusCode = statusCode;
        this._download = pdfFileName ? this._id : null;
        this._preview = pdfFileName ? this._id : null;
        this._valuta = valuta;
        this._forsinketDager = this._setPaymentDelay(
            sumUtestaaendeForsinketDager,
            forfallsDato,
        );
    }

    private _setPaymentDelay(
        sumUtestaaendeForsinketDager: number,
        forfallsDato: string,
    ) {
        if (sumUtestaaendeForsinketDager === 0) {
            return 0;
        }

        if (sumUtestaaendeForsinketDager > 0) {
            return sumUtestaaendeForsinketDager;
        }

        return differenceInDays(new Date(), new Date(forfallsDato));
    }
}
